'use client'

import { useOfflineSync } from '@/hooks/use-offline-sync'
import { Cloud, CloudOff, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react'
import { useState, useEffect } from 'react'
import { RelativeTime } from '@/components/shared/relative-time'

/**
 * Sync Status Indicator
 *
 * Shows the current offline queue and sync status:
 * - Online with no queued items: Hidden or green check
 * - Online with queued items: Shows syncing status
 * - Offline with queued items: Shows queue count
 * - Sync errors: Shows warning with retry option
 *
 * Also listens for "trigger-manual-sync" custom events from the
 * OfflineIndicator's "Sync Now" button.
 */
export function SyncStatusIndicator() {
  const {
    isSyncing,
    queueSize,
    lastSyncResults,
    lastSyncTime,
    openConflictCount,
    offlineFirstEnabled,
    triggerSync,
  } = useOfflineSync()

  const [isOnline, setIsOnline] = useState(true)
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    // Defer initial state sync to avoid cascading renders within the effect body
    queueMicrotask(() => {
      setIsOnline(navigator.onLine)
    })

    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    // Listen for manual sync trigger from OfflineIndicator "Sync Now" button
    const handleManualSync = () => {
      if (navigator.onLine && !isSyncing) {
        triggerSync()
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('trigger-manual-sync', handleManualSync)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('trigger-manual-sync', handleManualSync)
    }
  }, [isSyncing, triggerSync])

  // Show success message briefly after successful sync (deferred to avoid sync setState in effect)
  useEffect(() => {
    const allSuccess = lastSyncResults.length > 0 && lastSyncResults.every(r => r.success)
    if (!allSuccess) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    queueMicrotask(() => {
      if (cancelled) return
      setShowSuccess(true)
      timer = setTimeout(() => setShowSuccess(false), 3000)
    })
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [lastSyncResults])

  // Calculate failed syncs
  const failedSyncs = lastSyncResults.filter(r => !r.success)
  const hasErrors = failedSyncs.length > 0

  // Kill-switch: hide entirely when offline-first is disabled
  if (!offlineFirstEnabled) {
    return null
  }

  // Don't show anything when online, no queue, no recent activity
  if (isOnline && queueSize === 0 && !showSuccess && !isSyncing && !hasErrors && openConflictCount === 0) {
    return null
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
        {/* Syncing state */}
        {isSyncing && (
          <div className="flex items-center gap-2 px-4 py-3 bg-navy-50 text-navy-700">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span className="text-sm font-medium">Syncing {queueSize} items...</span>
          </div>
        )}

        {/* Open conflicts from failed replay */}
        {isOnline && openConflictCount > 0 && !isSyncing && (
          <div className="flex items-center gap-2 px-4 py-3 bg-orange-50 text-orange-800 border-b border-orange-100">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm font-medium">
              {openConflictCount} sync conflict{openConflictCount > 1 ? "s" : ""} — review queued work
            </span>
          </div>
        )}

        {/* Offline with queued items */}
        {!isOnline && queueSize > 0 && !isSyncing && (
          <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 text-amber-700">
            <CloudOff className="h-4 w-4" />
            <span className="text-sm font-medium">
              {queueSize} {queueSize === 1 ? 'order' : 'orders'} queued
            </span>
            <span className="text-xs text-amber-600">Will sync when online</span>
          </div>
        )}

        {/* Success message */}
        {showSuccess && isOnline && !isSyncing && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 text-green-700">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm font-medium">All changes synced</span>
            {lastSyncTime && (
              <RelativeTime value={lastSyncTime} className="text-xs text-green-600" />
            )}
          </div>
        )}

        {/* Error state with retry */}
        {hasErrors && isOnline && !isSyncing && !showSuccess && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 text-red-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm font-medium">
              {failedSyncs.length} {failedSyncs.length === 1 ? 'sync' : 'syncs'} failed
            </span>
            <button
              onClick={() => triggerSync()}
              className="ml-2 text-xs underline hover:no-underline min-h-[32px] px-2"
            >
              Retry
            </button>
          </div>
        )}

        {/* Online with pending queue (shouldn't happen often) */}
        {isOnline && queueSize > 0 && !isSyncing && !showSuccess && !hasErrors && (
          <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 text-slate-700">
            <Cloud className="h-4 w-4" />
            <span className="text-sm font-medium">
              {queueSize} order{queueSize > 1 ? 's' : ''} pending
            </span>
            <button
              onClick={() => triggerSync()}
              className="ml-2 text-xs text-navy-600 underline hover:no-underline min-h-[32px] px-2"
            >
              Sync now
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
